import { withDevtools } from '@angular-architects/ngrx-toolkit';
import { inject, Signal } from '@angular/core';
import { DashboardStats, UserInactivityDashboardStats } from '@models/dashboard/dashboard.model';
import { patchState, signalStore, withMethods, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { catchError, EMPTY, pipe, switchMap, tap } from 'rxjs';
import { DashboardService } from './dashboard.service';

type DashboardState = {
  userInactivityStats: UserInactivityDashboardStats | null;
  userInactivityStatsLoading: boolean;
  baseStats: DashboardStats | null;
  baseStatsLoading: boolean;
};

const initialState: DashboardState = {
  userInactivityStats: null,
  userInactivityStatsLoading: false,
  baseStats: null,
  baseStatsLoading: false,
};

export const DashboardStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withDevtools('dashboard-store'),
  withMethods((store, dashboardService = inject(DashboardService)) => {
    const repositoryMethods = {
      getUserInactivityStats: rxMethod<void>(
        pipe(
          tap(() => patchState(store, { userInactivityStatsLoading: true })),
          switchMap(() =>
            dashboardService.getUserStats().pipe(
              tap(userInactivityStats => patchState(store, { userInactivityStats, userInactivityStatsLoading: false })),
              catchError(() => {
                patchState(store, { userInactivityStatsLoading: false });
                return EMPTY;
              }),
            ),
          ),
        ),
      ),

      getBaseStats: rxMethod<void>(
        pipe(
          tap(() => patchState(store, { baseStatsLoading: true })),
          switchMap(() =>
            dashboardService.getStats().pipe(
              tap(baseStats => patchState(store, { baseStats, baseStatsLoading: false })),
              catchError(() => {
                patchState(store, { baseStatsLoading: false });
                return EMPTY;
              }),
            ),
          ),
        ),
      ),
    };
    return {
      ...repositoryMethods,
    };
  }),
);

export interface DashboardStoreType {
  readonly userInactivityStats: Signal<UserInactivityDashboardStats | null>;
  readonly baseStats: Signal<DashboardStats | null>;
  readonly baseStatsLoading: Signal<boolean>;
  readonly userInactivityStatsLoading: Signal<boolean>;

  readonly getUserInactivityStats: () => void;
  readonly getBaseStats: () => void;
}
